/**
 * Visibility-state filtering adapter.
 *
 * DYN-1.1 publishes `getVisibleEntityIds(meta, allIds)` which computes
 * the union of hidden entity IDs across every Visibility parameter's
 * currently-active state. We wrap it in an array filter that keeps the
 * block-entity order intact (important — renderBlockChildEntity walks
 * entities positionally, so reorder would change z-order).
 *
 * Keeping this adapter separate from action application is important:
 * actions operate on authored positions, then visibility hides the
 * result. Reversing the order would make a Move action that pushes a
 * hidden entity produce inconsistent output across show/hide toggles.
 *
 * ── Array-action copy visibility (local Codex review, P2, pre-PR) ──
 *
 * `evaluator.ts`'s Array-action branches (independent AND chain-group)
 * expand ONE source entity into N copies: the FIRST copy (index 0) reuses
 * the source's OWN authored id in place; every EXTRA copy (index >= 1)
 * gets a synthetic `${sourceId}#arr${n}` id (`expandArrayCopies` /
 * `expandGridCopies` never touch `.id` themselves — the caller assigns it).
 * A Visibility state's `invisibleEntityIds` is authored data, fixed at
 * BEDIT/import time — it can only ever list AUTHORED ids, never a
 * synthetic array-copy id (which does not exist until THIS evaluation
 * run). Filtering by `e.id` verbatim therefore always finds the extra
 * copies "not hidden" even when their SOURCE entity IS hidden by the
 * active state — a block combining an Array action with a Visibility
 * state that hides the array's source (a common real pattern: e.g. a
 * "row of screws" whose whole row toggles with a state) would render
 * phantom copies.
 *
 * ── Provenance marker, not id-string guessing (GitHub Codex review, P2,
 *    round 2 of the round-2 fix) ──
 *
 * The FIRST fix here (below, still kept as a fallback) resolved a copy's
 * source by pattern-matching its OWN id string — first the bare suffix
 * heuristic (`arraySourceId`), then an `authoredIds`-checked refinement so
 * a hand-authored id shaped like `part#arr1` is never mistaken for a copy
 * of `part`. GitHub Codex found the refinement was STILL incomplete: when
 * a block has BOTH an authored entity literally named e.g. `part#arr1`
 * AND an Array action on `part` that happens to generate a synthetic
 * extra with the SAME computed id `part#arr1`, `evaluateBlock`'s output
 * contains TWO DIFFERENT ENTITY OBJECTS sharing the identical `.id`
 * string — a case no id-string heuristic (however refined) can
 * disambiguate, because the ambiguity is IN the string itself, not in how
 * it is parsed.
 *
 * The robust fix tracks provenance at the point of CREATION instead of
 * re-deriving it later by inspecting the id: `evaluator.ts` stamps every
 * synthetic extra copy with an `__arrayCopySourceId` own-property (module
 * doc there) the moment it is built, BEFORE any id collision could even
 * arise. `resolveVisibilityId` checks that marker FIRST — unambiguous,
 * since it identifies THIS SPECIFIC object, not a string pattern — so an
 * authored `part#arr1` (no marker) and a synthetic `part#arr1` copy
 * (marker = "part") now resolve to DIFFERENT keys even though their `.id`
 * fields are byte-identical. The marker survives every downstream
 * transform in this pipeline (constraint-stretch, constraint-solver)
 * because they all spread `{...entity, <changed fields>}` rather than
 * reconstructing a narrow field allowlist — verified against
 * `actions.ts`'s translate/stretch/rotate/scale/flip helpers and
 * `constraintStretch.ts`'s `applyAxialStretch`, all of which preserve
 * unknown own-properties through a spread. `authoredIds` / the bare
 * suffix heuristic remain as fallbacks for entities that never carry the
 * marker (hand-constructed test fixtures that exercise this filter
 * directly, without going through the real evaluator pipeline).
 */

import type { DynamicBlockMetadata } from "@dxf-ai-editor/drawing-model";
import { getVisibleEntityIds } from "@dxf-ai-editor/drawing-model";

import type { EntityLike } from "./geometry";

/**
 * Own-property key `evaluator.ts` stamps on every synthetic array-copy
 * entity at creation time, holding the AUTHORED source id it was copied
 * from. `EntityLike = Record<string, unknown> & {type: string}` already
 * permits an arbitrary string key, so no cast is needed to write or read
 * it. Exported so `evaluator.ts` (the one place that creates copies) and
 * this module (the one place that consumes the marker) share the exact
 * key string — never hand-typed twice.
 */
export const ARRAY_COPY_SOURCE_MARKER = "__arrayCopySourceId";

/** Matches the synthetic suffix `evaluator.ts` appends to an EXTRA
 *  array-action copy's id (`${sourceId}#arr${n}`, n >= 1). Fallback only
 *  — see module doc "Provenance marker, not id-string guessing". */
const ARRAY_COPY_SUFFIX = /#arr\d+$/;

/**
 * Recover the AUTHORED source id a synthetic array-copy id was derived
 * from, so it can be checked against `invisibleEntityIds`. A no-op
 * (identity) for any id that is not an array-copy suffix. Fallback layer
 * only — see module doc; the marker-based resolution in
 * `resolveVisibilityId` is authoritative whenever it is available.
 */
function arraySourceId(id: string): string {
  return id.replace(ARRAY_COPY_SUFFIX, "");
}

/**
 * Resolve the id to check against `invisibleEntityIds` for ONE evaluated
 * entity. Three layers, most authoritative first (module doc for the
 * full rationale of each):
 *
 *   1. `ARRAY_COPY_SOURCE_MARKER` own-property — set at creation time by
 *      `evaluator.ts` for a REAL synthetic array copy. Unambiguous: it
 *      identifies this exact object, not a string shape, so it correctly
 *      distinguishes a synthetic copy from an authored entity even when
 *      their `.id` strings are byte-identical.
 *   2. `authoredIds` exact membership — the id is a genuinely authored
 *      entity (present in the block's own static `entities[]`), checked
 *      as itself, never suffix-stripped.
 *   3. `arraySourceId` bare suffix heuristic — last-resort fallback for
 *      callers with neither the marker nor an authored-id set (e.g. a
 *      unit test constructing raw entities to exercise this filter
 *      directly).
 */
function resolveVisibilityId(
  entity: EntityLike & { id: string },
  authoredIds: ReadonlySet<string> | undefined,
): string {
  const marker = entity[ARRAY_COPY_SOURCE_MARKER];
  if (typeof marker === "string") return marker;
  if (authoredIds?.has(entity.id)) return entity.id;
  return arraySourceId(entity.id);
}

export function filterVisibleEntities<T extends EntityLike & { id: string }>(
  entities: readonly T[],
  meta: DynamicBlockMetadata,
  /**
   * The block definition's STATIC, pre-evaluation entity ids
   * (`evaluateBlock`'s `blockDef.entities.map(e => e.id)`) — lets this
   * filter distinguish a genuinely authored id from a synthetic
   * array-copy id when the two collide in shape and neither carries the
   * creation-time marker (see `resolveVisibilityId`'s doc comment).
   * Optional for callers/tests that only need the pre-existing
   * suffix-heuristic behaviour.
   */
  authoredIds?: ReadonlySet<string>,
): readonly T[] {
  // Fast path — no visibility parameters means nothing to filter.
  const hasVisibility = meta.parameters.some((p) => p.kind === "visibility");
  if (!hasVisibility) return entities;

  const sourceIds = entities.map((e) => resolveVisibilityId(e, authoredIds));
  const visibleSourceIds = getVisibleEntityIds(meta, sourceIds);
  return entities.filter((_, i) => visibleSourceIds.has(sourceIds[i]!));
}
